import { useRef, useState } from "react";

const toFa = (n: string) =>
  n.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[+d]);

const toEn = (n: string) =>
  n.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));

const LETTERS = "الفبپتثجچحخدذرزژسشصضطظعغفقکگلمنوهی".split("");

interface PlateValue {
  left: string;
  letter: string;
  right: string;
  city: string;
}

export default function IranPlateInput({
  valueText,
  onChange,
  readOnly = false,
}: {
  valueText: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  const [showLetters, setShowLetters] = useState(false);
  const valueArray = valueText.split('|');

  const [value, setValue] = useState<PlateValue>(
    { left: valueArray[0] ?? '', letter: valueArray[1] ?? '', right: valueArray[2] ?? '', city: valueArray[3] ?? '' }
  );
  const letterRef = useRef<HTMLButtonElement>(null);

  const update = (key: keyof PlateValue, val: string) => {
    if (readOnly) return;
    setValue({ ...value, [key]: val });
    const plate = { ...value, [key]: val };
    onChange(plate.left + "|" + plate.letter + "|" + plate.right + "|" + plate.city);
  };

  const handleNum = (
    key: keyof PlateValue,
    val: string,
    max: number,
    nextRef?: React.RefObject<HTMLElement | null>
  ) => {
    if (readOnly) return;
    const en = toEn(val).replace(/\D/g, "").slice(0, max);
    update(key, toFa(en));
    if (en.length === max && nextRef?.current) nextRef.current.focus();
  };

  const rightRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);

  const containerClass = readOnly
    ? "flex items-center gap-1 bg-gray-50 border-2 border-gray-300 rounded-lg px-3 py-2 text-xl font-bold shadow select-none"
    : "flex items-center gap-1 bg-white border-2 border-gray-800 rounded-lg px-3 py-2 text-xl font-bold shadow";

  return (
    <div className="inline-flex flex-col items-center gap-2 font-[Vazirmatn,sans-serif]" dir="ltr">
      <div className={containerClass}>
        <input
          value={value.left}
          onChange={(e) => handleNum("left", e.target.value, 2, letterRef)}
          inputMode="numeric"
          maxLength={2}
          placeholder="۰۰"
          readOnly={readOnly}
          className={`w-10 text-center outline-none bg-transparent placeholder-gray-300 ${readOnly ? "cursor-default text-gray-600" : ""}`}
        />

        <div className="relative">
          <button
            ref={letterRef}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && setShowLetters((s) => !s)}
            className={`w-8 text-center border-b-2 outline-none bg-transparent ${
              readOnly
                ? "text-gray-600 border-gray-300 cursor-default"
                : "text-blue-700 border-blue-400"
            }`}
          >
            {value.letter || <span className="text-gray-300 text-base">ب</span>}
          </button>
          {!readOnly && showLetters && (
            <div className="absolute top-10 right-0 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-2 grid grid-cols-6 gap-1 w-56">
              {LETTERS.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => {
                    update("letter", l);
                    setShowLetters(false);
                    rightRef.current?.focus();
                  }}
                  className={`text-center py-1 rounded-md text-sm hover:bg-blue-100 transition-colors ${
                    value.letter === l ? "bg-blue-600 text-white" : ""
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
        </div>

        <input
          ref={rightRef}
          value={value.right}
          onChange={(e) => handleNum("right", e.target.value, 3, cityRef)}
          inputMode="numeric"
          maxLength={3}
          placeholder="۰۰۰"
          readOnly={readOnly}
          className={`w-14 text-center outline-none bg-transparent placeholder-gray-300 ${readOnly ? "cursor-default text-gray-600" : ""}`}
        />

        <span className="text-gray-400 mx-1">|</span>

        <div className="flex flex-col items-center">
          <input
            ref={cityRef}
            value={value.city}
            onChange={(e) => handleNum("city", e.target.value, 2)}
            inputMode="numeric"
            maxLength={2}
            placeholder="۰۰"
            readOnly={readOnly}
            className={`w-10 text-center outline-none bg-transparent placeholder-gray-300 ${readOnly ? "cursor-default text-gray-600" : ""}`}
          />
          <span className="text-[10px] text-gray-500 leading-none">ایران</span>
        </div>
      </div>
    </div>
  );
}
